/**
 * The chime layer: a transparent WebGL2 scene (scene.ts) drawn through the de-rez post
 * pass (post.ts) onto #chimes. Keeps the contract main.ts depends on: exact
 * worldToScreen / screenToWorld for the hands, addWave for strikes, setParallax.
 *
 * ?debug=render fires a wave at a random tube every 2 s and on click, and logs frame times.
 */
import { ACESFilmicToneMapping, SRGBColorSpace, Vector2, WebGLRenderer } from 'three';
import type { Bus } from '../bus';
import type { ChimeWorld, Quality, Vec2, Vec3 } from '../types';
import { v2, v3 } from '../types';
import { CONFIG } from '../config';
import { ChimeScene } from './scene';
import { DerezPost } from './post';
import { CAMERA } from './camera';

export interface RendererOptions {
  canvas: HTMLCanvasElement;
  world: ChimeWorld;
  quality: Quality;
  bus: Bus;
}

export interface RendererHandle {
  /** copy world state into the scene (called once per frame before render) */
  update(world: ChimeWorld, dt: number): void;
  /** draw the chime layer for time `now` (seconds) */
  render(now: number): void;
  resize(): void;
  /** world -> CSS px */
  worldToScreen(p: Vec3, out?: Vec2): Vec2;
  /** CSS px -> world point on the hand plane z = CONFIG.hand.planeZ */
  screenToWorld(x: number, y: number, out?: Vec3): Vec3;
  /** start a de-rez wave ring at a screen position (CSS px) */
  addWave(x: number, y: number, strength: number): void;
  /** gentle camera parallax from the primary pointer, both in -1..1 */
  setParallax(nx: number, ny: number): void;
  dispose(): void;
}

export const RENDER = {
  /** hard cap on drawing-buffer pixels per tier; DPR is lowered to stay under it */
  maxPixels: { full: 5.6e6, lite: 2.4e6 },
} as const;

export function createRenderer(opts: RendererOptions): RendererHandle {
  let renderer: WebGLRenderer;
  try {
    renderer = new WebGLRenderer({
      canvas: opts.canvas, alpha: true, antialias: false, powerPreference: 'high-performance',
      premultipliedAlpha: true, depth: false, stencil: false,
    });
  } catch (err) {
    console.warn('[render] WebGL2 unavailable; the chimes stay invisible but still play', err);
    return createFallback(opts);
  }
  return createWebGL(renderer, opts);
}

function createWebGL(renderer: WebGLRenderer, opts: RendererOptions): RendererHandle {
  const { canvas, world, quality } = opts;
  const q = new URLSearchParams(location.search);
  const debug = q.get('debug') === 'render';
  const slow = debug ? Number(q.get('slow')) || 1 : 1;

  renderer.setClearColor(0x000000, 0);
  renderer.autoClear = true;
  renderer.toneMapping = ACESFilmicToneMapping; // applied in the post pass; set for direct-render parity
  renderer.outputColorSpace = SRGBColorSpace;

  const scene = new ChimeScene(renderer, world, quality);
  const post = new DerezPost(renderer, quality);
  post.timeScale = 1 / slow;
  const bufSize = new Vector2();

  function resize() {
    const w = Math.max(1, innerWidth), h = Math.max(1, innerHeight);
    let dpr = Math.min(devicePixelRatio || 1, quality.dprCap);
    const cap = RENDER.maxPixels[quality.tier];
    if (w * h * dpr * dpr > cap) dpr = Math.max(1, Math.sqrt(cap / (w * h)));
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    renderer.getDrawingBufferSize(bufSize);
    scene.resize(w, h);
    post.resize(bufSize.x, bufSize.y, h, bufSize.y / h);
  }
  resize();

  // -- debug: waves without physics, and frame timing --------------------------------
  let timer = 0;
  let onDown: ((e: PointerEvent) => void) | null = null;
  const frameTimes: number[] = [];
  let lastLog = 0;
  let lastFrame = 0;
  if (debug) {
    const p = v3();
    const s = v2();
    const fire = () => {
      const i = Math.floor(Math.random() * world.tubes.length);
      world.tubePoint(i, 0.35 + Math.random() * 0.5, p);
      scene.cam.worldToScreen(p, s);
      post.addWave(s.x, s.y, 0.55 + Math.random() * 0.45);
      world.tubeStates[i].energy = Math.min(1, world.tubeStates[i].energy + 0.7);
    };
    timer = window.setInterval(fire, 2000 * slow);
    onDown = (e) => post.addWave(e.clientX, e.clientY, 1);
    window.addEventListener('pointerdown', onDown);
    console.info(`[render] debug: tier=${quality.tier} target=${post.targetType} msaa=${quality.tier === 'full' ? 4 : 0} buffer=${bufSize.x}x${bufSize.y} fov=${scene.cam.camera.fov.toFixed(1)} halfFloat=${post.targetType === 'half-float'}`);
  }

  return {
    update(w, dt) { scene.update(w, dt); },
    render(t) {
      post.render(scene.scene, scene.cam.camera, t);
      if (debug) {
        const pn = performance.now();
        if (lastFrame) frameTimes.push(pn - lastFrame);
        lastFrame = pn;
        if (pn - lastLog > 3000 && frameTimes.length > 10) {
          const sorted = frameTimes.slice().sort((a, b) => a - b);
          const avg = sorted.reduce((a, b) => a + b, 0) / sorted.length;
          const p95 = sorted[Math.floor(sorted.length * 0.95)];
          console.info(`[render] frame avg ${avg.toFixed(2)} ms (${(1000 / avg).toFixed(0)} fps), p95 ${p95.toFixed(2)} ms, waves ${post.activeWaves}, n=${sorted.length}`);
          frameTimes.length = 0;
          lastLog = pn;
        }
      }
    },
    resize,
    worldToScreen: (p, out) => scene.cam.worldToScreen(p, out),
    screenToWorld: (x, y, out) => scene.cam.screenToWorld(x, y, out),
    addWave(x, y, strength) { post.addWave(x, y, strength); },
    setParallax(nx, ny) { scene.cam.setParallax(nx, ny); },
    dispose() {
      if (timer) clearInterval(timer);
      if (onDown) window.removeEventListener('pointerdown', onDown);
      post.dispose();
      scene.dispose();
      renderer.dispose();
      void canvas;
    },
  };
}

/** No WebGL2: draw nothing, but keep the hands landing where the pointer is. */
function createFallback(opts: RendererOptions): RendererHandle {
  let w = 1, h = 1, focal = 1;
  const cam = CAMERA.lookAt;
  const dist = CAMERA.distance;
  function resize() {
    w = innerWidth; h = innerHeight;
    focal = (h / 2) / Math.tan((CAMERA.fovDeg * Math.PI) / 360);
    opts.canvas.width = 1; opts.canvas.height = 1;
  }
  resize();
  return {
    update() {},
    render() {},
    resize,
    worldToScreen(p, out = v2()) {
      const dz = Math.max(1e-3, cam.z + dist - p.z);
      out.x = w / 2 + (focal * (p.x - cam.x)) / dz;
      out.y = h / 2 - (focal * (p.y - cam.y)) / dz;
      return out;
    },
    screenToWorld(x, y, out = v3()) {
      const dz = cam.z + dist - CONFIG.hand.planeZ;
      out.x = cam.x + ((x - w / 2) * dz) / focal;
      out.y = cam.y - ((y - h / 2) * dz) / focal;
      out.z = CONFIG.hand.planeZ;
      return out;
    },
    addWave() {},
    setParallax() {},
    dispose() {},
  };
}
